import { generatePDFBlob } from '../services/pdfSendService';
import { getTrainingMealSchedule } from './mealSchedule';

interface WeeklyMealPlan {
  monday: DayMeals;
  tuesday: DayMeals;
  wednesday: DayMeals;
  thursday: DayMeals;
  friday: DayMeals;
  saturday: DayMeals;
  sunday: DayMeals;
}

interface DayMeals {
  breakfast: string[];
  morningSnack: string[];
  lunch: string[];
  afternoonSnack: string[];
  dinner: string[];
  eveningSnack: string[];
}

interface MealSubstitution {
  mealType: string;
  day?: string;
  options: Array<{
    id: string;
    foods: string[];
    calories: number;
    macros: { protein: number; carbohydrates: number; fats: number };
  }>;
}

interface FoodSubstitution {
  originalFood: string;
  mealType: string;
  day?: string;
  alternatives: Array<{
    food: string;
    quantity: string;
    equivalentMacros: boolean;
  }>;
}

interface MealPlanSubstitutions {
  mealSubstitutions?: MealSubstitution[];
  foodSubstitutions?: FoodSubstitution[];
}

interface MealPlanData {
  name: string;
  goal?: string;
  notes?: string;
  trainingTime?: string;
  targetCalories?: number;
  protein?: number;
  carbohydrates?: number;
  fats?: number;
  weeklyPlan?: WeeklyMealPlan;
  hydration?: string;
  substitutions?: MealPlanSubstitutions;
}

const DAY_LABELS: Record<string, string> = {
  monday: 'Segunda-feira',
  tuesday: 'Terça-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sábado',
  sunday: 'Domingo',
};

const MEAL_LABELS: Record<string, string> = {
  breakfast: 'Café da Manhã',
  morningSnack: 'Lanche da Manhã',
  lunch: 'Almoço',
  afternoonSnack: 'Lanche da Tarde',
  dinner: 'Jantar',
  eveningSnack: 'Ceia',
};

const MEAL_ORDER = ['breakfast', 'morningSnack', 'lunch', 'afternoonSnack', 'dinner', 'eveningSnack'];

export async function generateMealPlanPDF(
  planData: MealPlanData,
  patientName: string,
  nutritionistName: string
): Promise<Blob> {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF();
  
  // Configurações otimizadas para reduzir espaço
  const margin = 10;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - (margin * 2);
  let yPos = margin + 5;

  // Cabeçalho compacto
  doc.setFontSize(16);
  doc.text('PLANO ALIMENTAR', pageWidth / 2, yPos, { align: 'center' });
  yPos += 7;

  doc.setFontSize(9);
  doc.text(`Paciente: ${patientName}`, margin, yPos);
  yPos += 4;
  doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, margin, yPos);
  yPos += 4;
  doc.text(`Nutricionista: ${nutritionistName}`, margin, yPos);
  yPos += 6;

  // Informações do plano compactas
  doc.setFontSize(11);
  doc.text(`Plano: ${planData.name}`, margin, yPos);
  yPos += 5;

  if (planData.goal) {
    doc.setFontSize(9);
    const goalText = doc.splitTextToSize(`Objetivo: ${planData.goal}`, maxWidth);
    doc.text(goalText, margin, yPos);
    yPos += goalText.length * 4;
  }

  // Macros em linha compacta
  if (planData.targetCalories) {
    yPos += 3;
    doc.setFontSize(9);
    const macros: string[] = [];
    macros.push(`Calorias: ${planData.targetCalories} kcal/dia`);
    
    if (planData.protein) {
      const proteinPercent = planData.targetCalories > 0 
        ? ((planData.protein * 4 / planData.targetCalories) * 100).toFixed(1) 
        : '0';
      macros.push(`Proteínas: ${planData.protein}g (${proteinPercent}%)`);
    }
    
    if (planData.carbohydrates) {
      const carbsPercent = planData.targetCalories > 0 
        ? ((planData.carbohydrates * 4 / planData.targetCalories) * 100).toFixed(1) 
        : '0';
      macros.push(`Carboidratos: ${planData.carbohydrates}g (${carbsPercent}%)`);
    }
    
    if (planData.fats) {
      const fatsPercent = planData.targetCalories > 0 
        ? ((planData.fats * 9 / planData.targetCalories) * 100).toFixed(1) 
        : '0';
      macros.push(`Gorduras: ${planData.fats}g (${fatsPercent}%)`);
    }
    
    const macrosText = macros.join(' | ');
    const splitMacros = doc.splitTextToSize(macrosText, maxWidth);
    doc.text(splitMacros, margin, yPos);
    yPos += splitMacros.length * 4;
  }

  if (planData.hydration) {
    doc.setFontSize(9);
    doc.text(`Hidratação: ${planData.hydration}`, margin, yPos);
    yPos += 4;
  }

  const trainingSchedule = planData.trainingTime ? getTrainingMealSchedule(planData.trainingTime) : null;
  if (trainingSchedule) {
    doc.setFontSize(9);
    const timing = `Treino: ${planData.trainingTime} | Pré-treino: ${trainingSchedule.preWorkoutTime} | Pós-treino: ${trainingSchedule.postWorkoutTime}`;
    const splitTiming = doc.splitTextToSize(timing, maxWidth);
    doc.text(splitTiming, margin, yPos);
    yPos += splitTiming.length * 4 + 2;
  }

  yPos += 3;

  // Plano semanal otimizado
  if (planData.weeklyPlan) {
    const days = Object.keys(planData.weeklyPlan) as Array<keyof WeeklyMealPlan>;
    
    for (const day of days) {
      const dayMeals = planData.weeklyPlan[day];
      const dayLabel = DAY_LABELS[day] || day;

      // Verificar se há refeições neste dia
      const hasMeals = MEAL_ORDER.some(mealKey => 
        dayMeals[mealKey as keyof DayMeals]?.length > 0
      );

      if (!hasMeals) continue;

      // Nova página se necessário (com margem maior)
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = margin + 5;
      }

      doc.setFontSize(10);
      doc.text(dayLabel, margin, yPos);
      yPos += 5;

      doc.setFontSize(8);
      for (const mealKey of MEAL_ORDER) {
        const meals = dayMeals[mealKey as keyof DayMeals];
        if (meals && meals.length > 0) {
          const mealLabel = MEAL_LABELS[mealKey] || mealKey;
          const scheduledTime = trainingSchedule?.times[mealKey as keyof typeof trainingSchedule.times];
          const trainingMarker = trainingSchedule?.preWorkoutMeal === mealKey ? ' (pré-treino)' : trainingSchedule?.postWorkoutMeal === mealKey ? ' (pós-treino)' : '';
          doc.text(`${mealLabel}${scheduledTime ? ` - ${scheduledTime}` : ''}${trainingMarker}:`, margin + 3, yPos);
          yPos += 4;
          
          for (const meal of meals) {
            // Usar largura maior e espaçamento menor
            const splitText = doc.splitTextToSize(`• ${meal}`, maxWidth - 8);
            doc.text(splitText, margin + 6, yPos);
            yPos += splitText.length * 3.5;
            
            // Verificar se precisa de nova página
            if (yPos > pageHeight - 20) {
              doc.addPage();
              yPos = margin + 5;
            }
          }
          yPos += 1;
        }
      }
      yPos += 2;
    }
  }

  // Substituições (Sugestões de Troca)
  if (planData.substitutions && ((planData.substitutions.mealSubstitutions?.length ?? 0) > 0 || (planData.substitutions.foodSubstitutions?.length ?? 0) > 0)) {
    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = margin + 5;
    }
    yPos += 5;
    doc.setFontSize(10);
    doc.setTextColor(46, 125, 50); // Verde escuro
    doc.text('🔄 Sugestões de Troca (Opções Alternativas)', margin, yPos);
    yPos += 6;
    doc.setTextColor(0, 0, 0); // Voltar ao preto

    // Substituições de refeições completas
    if (planData.substitutions.mealSubstitutions && planData.substitutions.mealSubstitutions.length > 0) {
      doc.setFontSize(9);
      doc.text('Opções Alternativas por Refeição:', margin, yPos);
      yPos += 5;

      planData.substitutions.mealSubstitutions.forEach((sub, idx) => {
        if (yPos > pageHeight - 50) {
          doc.addPage();
          yPos = margin + 5;
        }

        const dayLabel = sub.day ? DAY_LABELS[sub.day] || sub.day : 'Todos os dias';
        const mealLabel = MEAL_LABELS[sub.mealType] || sub.mealType;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(`${mealLabel} (${dayLabel}):`, margin + 3, yPos);
        yPos += 4;
        doc.setFont('helvetica', 'normal');

        sub.options.forEach((option, optIdx) => {
          if (yPos > pageHeight - 30) {
            doc.addPage();
            yPos = margin + 5;
          }
          doc.setFontSize(7);
          doc.text(`  Opção ${optIdx + 1}:`, margin + 6, yPos);
          yPos += 3.5;
          option.foods.forEach((food) => {
            const splitText = doc.splitTextToSize(`    • ${food}`, maxWidth - 12);
            doc.text(splitText, margin + 9, yPos);
            yPos += splitText.length * 3;
            if (yPos > pageHeight - 20) {
              doc.addPage();
              yPos = margin + 5;
            }
          });
          doc.setFontSize(6);
          doc.setTextColor(100, 100, 100);
          const macroText = `${option.calories} kcal | P:${option.macros.protein}g C:${option.macros.carbohydrates}g G:${option.macros.fats}g`;
          doc.text(macroText, margin + 9, yPos);
          yPos += 3;
          doc.setTextColor(0, 0, 0);
        });
        yPos += 2;
      });
    }

    // Substituições de alimentos individuais
    if (planData.substitutions.foodSubstitutions && planData.substitutions.foodSubstitutions.length > 0) {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = margin + 5;
      }
      yPos += 3;
      doc.setFontSize(9);
      doc.text('Substituições de Alimentos:', margin, yPos);
      yPos += 5;

      planData.substitutions.foodSubstitutions.forEach((sub) => {
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = margin + 5;
        }
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        const dayLabel = sub.day ? DAY_LABELS[sub.day] || sub.day : '';
        const mealLabel = MEAL_LABELS[sub.mealType] || sub.mealType;
        doc.text(`${sub.originalFood} (${mealLabel}${dayLabel ? ' - ' + dayLabel : ''}):`, margin + 3, yPos);
        yPos += 4;
        doc.setFont('helvetica', 'normal');
        sub.alternatives.forEach((alt) => {
          doc.setFontSize(7);
          const altText = `  • ${alt.food} (${alt.quantity})`;
          const splitText = doc.splitTextToSize(altText, maxWidth - 12);
          doc.text(splitText, margin + 6, yPos);
          yPos += splitText.length * 3;
          if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = margin + 5;
          }
        });
        yPos += 2;
      });
    }
  }

  // Observações compactas
  if (planData.notes) {
    if (yPos > pageHeight - 30) {
      doc.addPage();
      yPos = margin + 5;
    }
    yPos += 2;
    doc.setFontSize(9);
    doc.text('Observações:', margin, yPos);
    yPos += 4;
    doc.setFontSize(8);
    const splitNotes = doc.splitTextToSize(planData.notes, maxWidth);
    doc.text(splitNotes, margin, yPos);
  }

  // Rodapé compacto
  const pageCount = doc.internal.pages.length - 1;
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(`NutriSync Web - Página ${page} de ${pageCount}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
  }

  const pdfBlob = await generatePDFBlob(doc);
  if (!pdfBlob.size || pdfBlob.type !== 'application/pdf') {
    throw new Error('O arquivo PDF foi criado vazio ou em um formato inválido. Tente novamente.');
  }
  return pdfBlob;
}











